import { useEffect, useState } from 'react';
import { useEditor } from '../../state/editorStore';
import { TemplatePicker } from '../TemplatePicker';
import { StudioTranscript } from './StudioTranscript';
import { StudioHookPanel } from './StudioHookPanel';
import { StudioHookMomentPanel } from './StudioHookMomentPanel';
import { StudioEditStylePanel } from './StudioEditStylePanel';
import { StudioTransitionsPanel } from './StudioTransitionsPanel';
import { StudioAudioPanel } from './StudioAudioPanel';
import { StudioBrollPanel } from './StudioBrollPanel';
import { StudioKmagicPanel, KmagicMark } from './StudioKmagicPanel';

/**
 * Left floating menu for /studio. A detached, rounded card that floats over the
 * stage (Submagic/CapCut style) with a horizontal tab bar. Same visual language
 * as the bottom panel - gray/white, no borders, no shadows, pink accent.
 */
type TabKey = 'kmagic' | 'styles' | 'emphasis' | 'takeover' | 'editstyle' | 'transcript' | 'broll' | 'audio' | 'transitions';

const TABS: { key: TabKey; label: string; icon: JSX.Element }[] = [
  { key: 'kmagic', label: 'kMagic (BETA)', icon: <KmagicMark size={22} /> },
  { key: 'styles', label: 'Styles', icon: <IconStyles /> },
  { key: 'editstyle', label: 'Edit style', icon: <IconEdit /> },
  { key: 'emphasis', label: 'Emphasis', icon: <IconHook /> },
  { key: 'takeover', label: 'Hooks', icon: <IconTakeover /> },
  { key: 'transcript', label: 'Transcript', icon: <IconTranscript /> },
  { key: 'broll', label: 'B-roll', icon: <IconBroll /> },
  { key: 'audio', label: 'Audio', icon: <IconAudio /> },
  { key: 'transitions', label: 'Transitions', icon: <IconTransitions /> },
];

export function StudioLeftPanel() {
  const [tab, setTab] = useState<TabKey>('styles');
  const setLeftTab = useEditor((s) => s.setLeftTab);
  // Mirror the open tab into the store so the timeline can show the B-roll lane.
  useEffect(() => {
    setLeftTab(tab);
    return () => setLeftTab('styles');
  }, [tab, setLeftTab]);

  const active = TABS.find((t) => t.key === tab)!;

  return (
    <>
      <nav className="studio-railfloat" role="tablist" aria-label="Editor tools">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={[
              'slp-rbtn',
              t.key === 'kmagic' && 'slp-rbtn-magic',
              tab === t.key && 'on',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setTab(t.key)}
            aria-label={t.label}
            data-tip={t.label}
          >
            {t.icon}
          </button>
        ))}
      </nav>

      <div className="studio-leftfloat">
        <div className="slp-head">{active.label}</div>
        <div className="slp-content">
        {tab === 'kmagic' && <StudioKmagicPanel />}
        {tab === 'styles' && (
          <div className="slp-body slp-styles">
            <TemplatePicker />
          </div>
        )}
        {tab === 'transcript' && <StudioTranscript />}
        {tab === 'emphasis' && <StudioHookPanel />}
        {tab === 'takeover' && <StudioHookMomentPanel />}
        {tab === 'editstyle' && <StudioEditStylePanel />}
        {tab === 'broll' && <StudioBrollPanel />}
        {tab === 'audio' && <StudioAudioPanel />}
        {tab === 'transitions' && <StudioTransitionsPanel />}
        </div>
      </div>
    </>
  );
}

/* ---- tab glyphs (currentColor, 18px) ---- */
function IconStyles() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 7h16M4 12h10M4 17h7" />
      <circle cx="18.5" cy="15.5" r="2.5" fill="currentColor" stroke="none" />
    </svg>
  );
}
function IconHook() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path className="ico-fill" d="M13 3l-2 9h5l-9 9 2-9H4l9-9Z" />
    </svg>
  );
}
function IconTakeover() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path className="ico-fill" d="M12 2l2.6 6.3L21 9l-5 4.2L17.5 20 12 16.4 6.5 20 8 13.2 3 9l6.4-.7L12 2Z" />
    </svg>
  );
}
function IconEdit() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M15 4l5 5" />
      <path className="ico-fill" d="M4 20l1-4L16 5l3 3L8 19l-4 1Z" />
    </svg>
  );
}
function IconTranscript() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path className="ico-fill" d="M5 4h14v16H5z" />
      <path className="ico-detail" d="M8 9h8M8 13h8M8 17h5" />
    </svg>
  );
}
function IconBroll() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect className="ico-fill" x="3" y="5" width="18" height="14" rx="2" />
      <path className="ico-detail" d="M3 15l5-4 4 3 3-2 6 4" />
      <circle className="ico-dot" cx="8" cy="9" r="1.3" fill="currentColor" stroke="none" />
    </svg>
  );
}
function IconAudio() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 10v4M8 6v12M12 3v18M16 7v10M20 10v4" />
    </svg>
  );
}
function IconTransitions() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path className="ico-fill" d="M4 5h7v14H4z" />
      <path d="M20 5h-7v14h7" />
      <path className="ico-detail" d="M13 12H9m0 0l2-2m-2 2l2 2" />
    </svg>
  );
}
